import React, { useEffect, useMemo, useRef, useState } from "react";
import Draggable from "react-draggable";
import LoadingButton from "../LoadingButton";
import { Sparkles } from "lucide-react";

const norm = (v) => (v ?? "").toString();
const isDraftLike = (s) => (s || "").toLowerCase() === "draft";
const DEFAULT_NA_REASON = "N/A reason not provided";

/** Draggable wrapper modal for NA suggestions (React 18 safe: nodeRef) */
function NaSuggestionsModal({ open, onClose, children }) {
  const nodeRef = useRef(null);
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* backdrop */}
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />

      {/* draggable modal */}
      <Draggable
        nodeRef={nodeRef}
        handle=".drag-handle"
        bounds="parent"
        cancel='input,textarea,button,select,option,[contenteditable="true"]'
      >
        <div
          ref={nodeRef}
          className="absolute left-1/2 top-24 -translate-x-1/2 w-[760px] max-w-[95vw] bg-white rounded-lg shadow-xl"
        >
          {/* Header = drag handle */}
          <div className="drag-handle cursor-move select-none px-4 py-3 border-b flex items-center justify-between">
            <div className="font-semibold">N/A Suggestions</div>
            <button
              type="button"
              onClick={onClose}
              className="px-2 py-1 rounded hover:bg-gray-100"
              aria-label="Close"
              title="Close"
            >
              ✕
            </button>
          </div>

          <div className="p-4">{children}</div>
        </div>
      </Draggable>
    </div>
  );
}

const ChecklistTab = ({
  detail,
  checklist,
  checklistLoading,
  checklistForbidden,
  onSaveDraft,
  savingDraft,
  onSuggestNa,
  onRewriteText,
}) => {
  // drafts: { [itemId]: { checked?, notes?, na?, na_reason?, hide_from_report? } }
  const [drafts, setDrafts] = useState({});
  const [showNaModal, setShowNaModal] = useState(false);
  const [naSuggestions, setNaSuggestions] = useState([]); // [{item_id, suggested_na, reason, apply}]
  const [loadingNa, setLoadingNa] = useState(false);
  const [applyingNa, setApplyingNa] = useState(false);
  const [polishing, setPolishing] = useState({}); // { [itemId]: bool }

  // NEW: rewrite preview modal state
  const [rewritePreview, setRewritePreview] = useState({
    open: false,
    itemId: null,
    original: "",
    rewritten: "",
  });

  useEffect(() => {
    setDrafts({});
  }, [checklist?.id]);

  const items = useMemo(() => checklist?.items ?? [], [checklist?.items]);

  // NEW: group items by inspection type based on existing item_category (no splitting)
  const itemsByType = useMemo(() => {
    const grouped = {};
    for (const it of items) {
      const typeName = (it.item_category || "General").trim();
      if (!grouped[typeName]) grouped[typeName] = [];
      grouped[typeName].push(it);
    }
    return grouped;
  }, [items]);

  // Lock rules:
  // - lock if checklist completed
  // - lock if inspection is NOT draft (submitted/approved/rejected/completed)
  const lockedByStatus = !!detail && !isDraftLike(detail.status);
  const locked = !!checklist?.completed || lockedByStatus;

  const getValue = (it) => {
    const d = drafts[it.id] || {};

    // Base values from DB
    const baseChecked = !!it.checked;
    const baseNa = !!it.na;

    // NEW: fallback to inspection_note if notes empty
    const baseNotes =
      (it.notes && it.notes.trim().length > 0
        ? it.notes
        : it.inspection_note || "") || "";

    const baseNaReason = it.na_reason || "";

    return {
      checked: d.checked ?? baseChecked,
      na: d.na ?? baseNa,
      na_reason: d.na_reason ?? baseNaReason,
      notes: d.notes ?? baseNotes,
      is_mandatory: it.is_mandatory,
      hide_from_report: d.hide_from_report ?? !!it.hide_from_report,
    };
  };

  // ⬇️ ADD THIS
  const getStatus = (it) => {
    const v = getValue(it);
    if (v.na) return "na";
    if (v.checked) return "checked";
    return "not_checked";
  };

  const updateDraft = (it, patch) => {
    setDrafts((prev) => {
      const current = prev[it.id] || {};
      const next = { ...current, ...patch };

      // If marking NA -> uncheck
      if (patch.na === true) {
        next.checked = false;
        const existingReason = norm(
          patch.na_reason ?? current.na_reason ?? it.na_reason,
        ).trim();
        if (!existingReason) {
          next.na_reason = DEFAULT_NA_REASON;
        }
      }

      // If checking -> clear NA + NA reason
      if (patch.checked === true) {
        next.na = false;
        next.na_reason = "";
      }

      return { ...prev, [it.id]: next };
    });
  };

  const isDirty = useMemo(() => {
    if (!checklist) return false;

    return Object.keys(drafts).some((id) => {
      const it = items.find((x) => x.id === Number(id));
      if (!it) return false;

      const d = drafts[id] || {};
      const checked = d.checked ?? !!it.checked;
      const na = d.na ?? !!it.na;
      const na_reason = norm(d.na_reason ?? it.na_reason);
      const notes = norm(d.notes ?? it.notes);
      const hide_from_report = d.hide_from_report ?? !!it.hide_from_report;

      return (
        checked !== !!it.checked ||
        na !== !!it.na ||
        na_reason !== norm(it.na_reason) ||
        hide_from_report !== !!it.hide_from_report ||
        notes !== norm(it.notes)
      );
    });
  }, [drafts, items, checklist]);

  const dirtyCount = useMemo(() => {
    if (!checklist) return 0;

    let n = 0;
    for (const [id, dRaw] of Object.entries(drafts)) {
      const it = items.find((x) => x.id === Number(id));
      if (!it) continue;

      const d = dRaw || {};
      const checked = d.checked ?? !!it.checked;
      const na = d.na ?? !!it.na;
      const na_reason = norm(d.na_reason ?? it.na_reason);
      const notes = norm(d.notes ?? it.notes);
      const hide_from_report = d.hide_from_report ?? !!it.hide_from_report;

      const changed =
        checked !== !!it.checked ||
        na !== !!it.na ||
        na_reason !== norm(it.na_reason) ||
        notes !== norm(it.notes) ||
        hide_from_report !== !!it.hide_from_report;

      if (changed) n += 1;
    }
    return n;
  }, [drafts, items, checklist]);

  const discardDrafts = () => setDrafts({});

  const saveDrafts = async () => {
    if (!checklist || dirtyCount === 0) return;

    const updates = Object.keys(drafts)
      .map((idStr) => {
        const id = Number(idStr);
        const it = items.find((x) => x.id === id);
        if (!it) return null;

        const v = getValue(it);

        const checked = !!v.checked;
        const na = !!v.na;

        return {
          id,
          checked: checked && !na, // never allow both true
          na: na && !checked,
          na_reason: checked ? "" : norm(v.na_reason || ""),
          notes: norm(v.notes || ""),
          hide_from_report: !!v.hide_from_report,
        };
      })
      .filter(Boolean)
      .filter((u) => {
        const it = items.find((x) => x.id === u.id);
        if (!it) return false;

        // compare against DB current values to only send true changes
        const changed =
          !!u.checked !== !!it.checked ||
          !!u.na !== !!it.na ||
          norm(u.na_reason) !== norm(it.na_reason) ||
          norm(u.notes) !== norm(it.notes) ||
          !!u.hide_from_report !== !!it.hide_from_report;

        return changed;
      });

    await onSaveDraft?.(updates);
    setDrafts({});
  };

  // Done = checked OR na
  const effectiveDone = (it) => {
    const d = drafts[it.id] || {};
    const checked = d.checked ?? !!it.checked;
    const na = d.na ?? !!it.na;
    return !!checked || !!na;
  };

  // Mandatory items only
  const mandatoryItems = useMemo(
    () => items.filter((it) => it.is_mandatory !== false),
    [items],
  );

  const allMandatoryDone = useMemo(() => {
    if (mandatoryItems.length === 0) return false; // or true if you prefer
    return mandatoryItems.every((it) => effectiveDone(it));
  }, [mandatoryItems, drafts]);

  // NA requires a reason OR notes
  const hasNaMissingReason = useMemo(() => {
    return items.some((it) => {
      const v = getValue(it);
      if (!v.na) return false;
      const reasonOk = norm(v.na_reason).trim().length > 0;
      const notesOk = norm(v.notes).trim().length > 0;
      return !reasonOk && !notesOk;
    });
  }, [items, drafts]);

  const aiBtnBase =
    "inline-flex items-center gap-2 rounded-md text-xs font-semibold text-white " +
    "shadow-sm disabled:opacity-50 disabled:cursor-not-allowed";
  const aiBtnGrad =
    "bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600";

  return (
    <div className="space-y-3">
      {checklistLoading && (
        <div className="text-sm text-gray-500">Loading checklist…</div>
      )}

      {!checklistLoading && checklistForbidden && (
        <div className="p-4 border rounded-md bg-yellow-50 text-yellow-800 text-sm">
          You don’t have permission to view checklist for this inspection.
        </div>
      )}

      {!checklistLoading && !checklistForbidden && checklist && (
        <>
          {/* Header / Draft bar */}
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <div className="text-sm font-medium text-gray-800">
                Checklist {checklist.completed ? "(Completed)" : ""}
              </div>
              <div className="text-xs text-gray-500">
                <b>{items.length}</b> checklist items
              </div>

              {lockedByStatus && (
                <div className="text-xs text-gray-500 mt-1">
                  Checklist is locked because inspection is {detail?.status}.
                </div>
              )}

              {!locked && dirtyCount > 0 && (
                <div className="text-xs text-orange-700 mt-1">
                  Unsaved changes: {dirtyCount}
                </div>
              )}

              {!locked && !allMandatoryDone && (
                <div className="text-xs text-red-600 mt-1">
                  All <b>mandatory</b> items should be (Checked , Not checked or
                  N/A).
                </div>
              )}

              {!locked && hasNaMissingReason && (
                <div className="text-xs text-red-600 mt-1">
                  Some N/A items are missing a reason (or notes).
                </div>
              )}

              {!locked &&
                isDirty &&
                allMandatoryDone &&
                !hasNaMissingReason && (
                  <div className="text-xs text-orange-700 mt-1">
                    Save your changes before leaving this checklist.
                  </div>
                )}
            </div>

            {/* Actions */}
            <div className="flex flex-col items-start gap-2 md:items-end">
              {/* Row 1: AI tools */}
              <div className="flex flex-wrap items-center gap-2">
                {/* AI: Suggest N/A */}
                <button
                  type="button"
                  disabled={locked || loadingNa || !onSuggestNa}
                  onClick={async () => {
                    if (!onSuggestNa) return;
                    setLoadingNa(true);
                    try {
                      const s = await onSuggestNa();
                      const normalized = (s || []).map((x) => ({
                        ...x,
                        apply: true,
                        reason: x.reason || "",
                      }));
                      setNaSuggestions(normalized);
                      setShowNaModal(true);
                    } finally {
                      setLoadingNa(false);
                    }
                  }}
                  className={[
                    "px-3 py-2",
                    aiBtnBase,
                    locked || loadingNa || !onSuggestNa
                      ? "bg-gray-100 text-gray-400 border border-gray-200"
                      : aiBtnGrad,
                  ].join(" ")}
                  title={
                    !onSuggestNa
                      ? "Not wired yet"
                      : "SmokyQ-AI suggests N/A items"
                  }
                >
                  <Sparkles className="w-4 h-4" />
                  {loadingNa ? "Thinking…" : "Suggest N/A"}
                </button>
              </div>

              {/* Row 2: Draft / workflow */}
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={discardDrafts}
                  disabled={locked || !isDirty || savingDraft}
                  className={[
                    "px-2.5 py-1.5 rounded-md text-xs border bg-white",
                    "disabled:opacity-50 disabled:cursor-not-allowed",
                    "hover:bg-gray-50",
                  ].join(" ")}
                  title="Discard unsaved changes"
                >
                  Discard
                </button>

                <LoadingButton
                  loading={savingDraft}
                  loadingText="Saving…"
                  disabled={locked || !isDirty || savingDraft}
                  onClick={saveDrafts}
                  className={[
                    "px-3 py-2",
                    aiBtnBase,
                    aiBtnGrad,
                    "hover:opacity-95",
                  ].join(" ")}
                  title="Save checklist changes"
                >
                  Save
                </LoadingButton>
              </div>
            </div>
          </div>

          {/* Grouped tables by inspection type (based on item_category) */}
          <div className="space-y-4">
            {Object.entries(itemsByType).map(([typeName, groupItems]) => (
              <div key={typeName} className="border rounded-md overflow-hidden">
                {/* Group header */}
                <div className="px-3 py-2 border-b bg-gray-50 text-sm font-semibold text-gray-800">
                  {typeName}
                </div>

                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-700">
                    <tr>
                      <th className="p-2 text-left w-24">Done</th>
                      <th className="p-2 text-left">Item</th>
                      <th className="p-2 text-left w-32">Mandatory</th>
                      <th className="p-2 text-left w-[40%] min-w-[320px]">
                        Notes / N/A reason
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {groupItems.map((it) => {
                      const v = getValue(it);

                      return (
                        <tr key={it.id} className="border-t align-top">
                          {/* Done controls */}
                          <td className="p-2 space-y-1">
                            {/* Checked */}
                            <label className="flex items-center gap-2">
                              <input
                                type="radio"
                                name={`status-${it.id}`}
                                value="checked"
                                checked={getStatus(it) === "checked"}
                                onChange={() =>
                                  updateDraft(it, { checked: true, na: false })
                                }
                                disabled={locked}
                              />
                              <span className="text-xs">Checked</span>
                            </label>

                            {/* Not checked */}
                            <label className="flex items-center gap-2">
                              <input
                                type="radio"
                                name={`status-${it.id}`}
                                value="not_checked"
                                checked={getStatus(it) === "not_checked"}
                                onChange={() =>
                                  updateDraft(it, {
                                    checked: false,
                                    na: false,
                                  })
                                }
                                disabled={locked}
                              />
                              <span className="text-xs">Not checked</span>
                            </label>

                            {/* N/A */}
                            <label className="flex items-center gap-2">
                              <input
                                type="radio"
                                name={`status-${it.id}`}
                                value="na"
                                checked={getStatus(it) === "na"}
                                onChange={() =>
                                  updateDraft(it, { na: true, checked: false })
                                }
                                disabled={locked}
                              />
                              <span className="text-xs">N/A</span>
                            </label>
                          </td>

                          {/* Item text */}
                          <td className="p-2">
                            <div>{it.item_text}</div>
                            {Array.isArray(it.requirement_refs) &&
                              it.requirement_refs.length > 0 && (
                                <details className="mt-2">
                                  <summary className="text-xs text-indigo-600 cursor-pointer">
                                    Source / Requirement
                                  </summary>
                                  <div className="mt-1 space-y-2 text-xs text-gray-600">
                                    {it.requirement_refs.map((ref) => (
                                      <div
                                        key={`${it.id}-${ref.requirement_id}`}
                                        className="rounded border bg-gray-50 p-2"
                                      >
                                        <div className="font-semibold text-gray-800">
                                          {ref.requirement_number || `Requirement #${ref.requirement_id}`}
                                        </div>
                                        <div className="mt-1">{ref.requirement}</div>
                                        {ref.acceptance_criteria && (
                                          <div className="mt-1">
                                            <b>Acceptance:</b> {ref.acceptance_criteria}
                                          </div>
                                        )}
                                        {(ref.sources || []).map((src, idx) => (
                                          <div key={idx} className="mt-1 text-gray-500">
                                            {[
                                              src.document_name,
                                              src.revision ? `Rev ${src.revision}` : null,
                                              src.section,
                                              src.clause,
                                              src.page ? `Page ${src.page}` : null,
                                              src.drawing_sheet,
                                            ]
                                              .filter(Boolean)
                                              .join(" · ")}
                                            {src.source_quote && (
                                              <div className="mt-1 italic">“{src.source_quote}”</div>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    ))}
                                  </div>
                                </details>
                              )}
                          </td>

                          {/* Mandatory badge */}
                          <td className="p-2">
                            <span
                              className={
                                v.is_mandatory
                                  ? "inline-flex items-center px-2 py-1 rounded bg-red-50 text-red-700 text-xs"
                                  : "inline-flex items-center px-2 py-1 rounded bg-gray-100 text-gray-700 text-xs"
                              }
                            >
                              {v.is_mandatory ? "Required" : "Optional"}
                            </span>
                          </td>

                          {/* Notes / N/A reason */}
                          <td className="p-2 space-y-2 w-[40%] min-w-[320px]">
                            {v.na && (
                              <input
                                value={v.na_reason}
                                onChange={(e) =>
                                  updateDraft(it, {
                                    na_reason: e.target.value,
                                  })
                                }
                                className="w-full border rounded-md px-2 py-1 text-sm"
                                placeholder="Reason for N/A… (required if no notes)"
                                disabled={locked}
                              />
                            )}

                            {/* NOTES + ✨ POLISH BUTTON */}
                            <div className="flex items-start gap-2">
                              <textarea
                                value={v.notes}
                                onChange={(e) =>
                                  updateDraft(it, { notes: e.target.value })
                                }
                                className="w-full border rounded-md px-2 py-1 text-sm"
                                placeholder={
                                  v.na ? "N/A reason or notes…" : "Notes…"
                                }
                                rows={2}
                                disabled={locked}
                              />

                              {/* polish button unchanged */}
                              <button
                                type="button"
                                disabled={
                                  locked ||
                                  !onRewriteText ||
                                  !!polishing[it.id] ||
                                  !norm(v.notes).trim()
                                }
                                onClick={async () => {
                                  /* ... */
                                }}
                                className="px-2 py-1 rounded-md border text-xs bg-white disabled:opacity-50"
                                title="Polish notes"
                              >
                                {polishing[it.id] ? "…" : "✨"}
                              </button>
                            </div>
                            <label className="inline-flex items-center gap-2 text-xs text-gray-600">
                              <input
                                type="checkbox"
                                checked={!!v.hide_from_report}
                                onChange={(e) =>
                                  updateDraft(it, {
                                    hide_from_report: e.target.checked,
                                  })
                                }
                                disabled={locked}
                              />
                              Hide from report
                            </label>

                            {v.na &&
                              norm(v.na_reason).trim() === "" &&
                              norm(v.notes).trim() === "" && (
                                <div className="text-xs text-red-600">
                                  Provide N/A reason or notes.
                                </div>
                              )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </div>

          {/* Rewrite Preview Modal (Before / After) */}
          {rewritePreview.open && (
            <div className="fixed inset-0 z-50">
              <div
                className="absolute inset-0 bg-black/30"
                onClick={() =>
                  setRewritePreview({
                    open: false,
                    itemId: null,
                    original: "",
                    rewritten: "",
                  })
                }
              />

              <div className="absolute left-1/2 top-24 -translate-x-1/2 w-[860px] max-w-[95vw] bg-white rounded-lg shadow-xl">
                <div className="px-4 py-3 border-b flex items-center justify-between">
                  <div className="font-semibold">Polished Notes Preview</div>
                  <button
                    type="button"
                    onClick={() =>
                      setRewritePreview({
                        open: false,
                        itemId: null,
                        original: "",
                        rewritten: "",
                      })
                    }
                    className="px-2 py-1 rounded hover:bg-gray-100"
                    aria-label="Close"
                    title="Close"
                  >
                    ✕
                  </button>
                </div>

                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="border rounded-md p-3">
                    <div className="text-xs font-medium text-gray-600 mb-2">
                      Before
                    </div>
                    <pre className="whitespace-pre-wrap text-sm text-gray-900 font-sans">
                      {rewritePreview.original}
                    </pre>
                  </div>

                  <div className="border rounded-md p-3 bg-gray-50">
                    <div className="text-xs font-medium text-gray-600 mb-2">
                      After
                    </div>
                    <pre className="whitespace-pre-wrap text-sm text-gray-900 font-sans">
                      {rewritePreview.rewritten}
                    </pre>
                  </div>
                </div>

                <div className="px-4 pb-4 pt-2 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    className="px-3 py-2 rounded-md border bg-white text-sm hover:bg-gray-50"
                    onClick={() =>
                      setRewritePreview({
                        open: false,
                        itemId: null,
                        original: "",
                        rewritten: "",
                      })
                    }
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    className="px-3 py-2 rounded-md bg-gray-900 text-white text-sm hover:opacity-95"
                    onClick={() => {
                      const itemId = rewritePreview.itemId;
                      const it = items.find((x) => x.id === itemId);
                      if (it)
                        updateDraft(it, { notes: rewritePreview.rewritten });

                      setRewritePreview({
                        open: false,
                        itemId: null,
                        original: "",
                        rewritten: "",
                      });
                    }}
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* N/A Suggestions Modal (DRAGGABLE, nodeRef) */}
          <NaSuggestionsModal
            open={showNaModal}
            onClose={() => setShowNaModal(false)}
          >
            <div className="space-y-3 max-h-[70vh] overflow-auto">
              {naSuggestions.length === 0 && (
                <div className="text-sm text-gray-600">No suggestions.</div>
              )}

              {naSuggestions.map((s) => {
                const it = items.find((x) => x.id === s.item_id);
                return (
                  <div key={s.item_id} className="border rounded-md p-3">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!!s.apply}
                        onChange={(e) => {
                          const v = e.target.checked;
                          setNaSuggestions((prev) =>
                            prev.map((x) =>
                              x.item_id === s.item_id ? { ...x, apply: v } : x,
                            ),
                          );
                        }}
                      />
                      <div>
                        <div className="font-medium text-sm">
                          {it?.item_text || "(item not found)"}
                        </div>
                        {!!it?.item_category && (
                          <div className="text-xs text-gray-500">
                            {it.item_category}
                          </div>
                        )}
                      </div>
                    </label>

                    <div className="mt-2">
                      <div className="text-xs text-gray-500 mb-1">
                        Reason (editable)
                      </div>
                      <textarea
                        value={s.reason || ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          setNaSuggestions((prev) =>
                            prev.map((x) =>
                              x.item_id === s.item_id ? { ...x, reason: v } : x,
                            ),
                          );
                        }}
                        className="w-full border rounded-md px-2 py-1 text-sm"
                        rows={2}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 pt-3 border-t flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowNaModal(false)}
                className="px-3 py-2 rounded-md border bg-white text-sm"
                disabled={applyingNa}
              >
                Cancel
              </button>

              <LoadingButton
                loading={applyingNa}
                loadingText="Applying…"
                disabled={locked || applyingNa || !onSaveDraft}
                onClick={async () => {
                  const updates = naSuggestions
                    .filter((s) => s.apply)
                    .map((s) => ({
                      id: s.item_id,
                      na: true,
                      na_reason: s.reason?.trim() || DEFAULT_NA_REASON,
                    }));

                  if (updates.length === 0) {
                    setShowNaModal(false);
                    return;
                  }

                  setApplyingNa(true);
                  try {
                    await onSaveDraft?.(updates);
                    setDrafts({});
                    setShowNaModal(false);
                    setNaSuggestions([]);
                  } finally {
                    setApplyingNa(false);
                  }
                }}
                className="px-3 py-2 rounded-md bg-gray-900 text-white text-sm disabled:opacity-50"
              >
                Apply selected
              </LoadingButton>
            </div>
          </NaSuggestionsModal>
        </>
      )}

      {!checklistLoading && !checklistForbidden && !checklist && (
        <div className="text-sm text-gray-500">No checklist data.</div>
      )}
    </div>
  );
};

export default React.memo(ChecklistTab);
